<script lang="ts">
  import { fade } from "svelte/transition";
  import PageLoader from "./page-loader.svelte";
  import { beforeNavigate, afterNavigate } from "$app/navigation";
  import { page } from "$app/stores";
  import { routes } from "$lib/routes";
  import { onMount, onDestroy } from "svelte";
  import { authStore, authStore as Current } from "$lib/stores";
  import { hamburgerMenuIcon } from "$lib/images/icons";
  import { useSidebar } from "$lib/components/ui/sidebar-custom/context.svelte";
  import { cn } from "$lib/utility";
  import logoNormal from "$lib/images/logo/logo_light.svg";
  import logoDark from "$lib/images/logo/logo_dark.svg";
  import { goto } from "$app/navigation";
  import { mode } from "mode-watcher";
  import {
    homepageStore,
    setShortcutMode,
    setShortcutSequence,
    resetShortcutState,
    createHomepageShortcuts,
  } from "$lib/stores/homepageStore";
  import { browser } from "$app/environment";

  import HeaderLogo from "./headerLogo.svelte";
  import HeaderNavigation from "./headerNavigation.svelte";

  type Props = {
    className?: string;
    variant?: "default" | "dark";
  };
  const { className = "", variant = "default" }: Props = $props();

  let logo = $derived(variant === "default" && $mode !== "dark" ? logoNormal : logoDark);

  const sidebar = useSidebar();
  const isSidebarOpen = $derived(sidebar?.open || false);
  const isSearchPage = $derived($page.url.pathname.startsWith("/search"));
  const isCampaignsPage = $derived($page.url.pathname.startsWith("/campaigns"));
  const isBlogPage = $derived($page.url.pathname.startsWith("/blog"));

  let isHeaderless = $derived((isSearchPage || isCampaignsPage) && Current.isLoggedIn);

  // top page preloader
  let navigationState: "loading" | "loaded" = $state("loaded");

  beforeNavigate(() => {
    navigationState = "loading";
  });

  afterNavigate(() => {
    navigationState = "loaded";
  });

  const isTablePage = $derived($page.url.pathname.startsWith("/table"));

  let featuresDropdownOpen = $state(false);
  let companyDropdownOpen = $state(false);
  let useCasesDropdownOpen = $state(false);

  const toggleFeaturesDropdown = () => {
    featuresDropdownOpen = !featuresDropdownOpen;
    if (featuresDropdownOpen) {
      companyDropdownOpen = false;
      useCasesDropdownOpen = false;
    }
  };

  const toggleCompanyDropdown = () => {
    companyDropdownOpen = !companyDropdownOpen;
    if (companyDropdownOpen) {
      featuresDropdownOpen = false;
      useCasesDropdownOpen = false;
    }
  };

  const toggleUseCasesDropdown = () => {
    useCasesDropdownOpen = !useCasesDropdownOpen;
    if (useCasesDropdownOpen) {
      featuresDropdownOpen = false;
      companyDropdownOpen = false;
    }
  };

  const closeDropdowns = () => {
    featuresDropdownOpen = false;
    companyDropdownOpen = false;
    useCasesDropdownOpen = false;
  };

  // Shortcut handling
  const isInShortcutMode = $derived($homepageStore.isInShortcutMode);
  const currentShortcutSequence = $derived($homepageStore.currentShortcutSequence);
  const homepageShortcuts = createHomepageShortcuts(authStore);

  // Handle keyboard events for shortcuts
  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isTablePage) {
      if (e.key === "Alt") {
        e.preventDefault();
        setShortcutMode(!isInShortcutMode);
        return;
      }

      if (isInShortcutMode) {
        e.preventDefault();
        e.stopPropagation();

        // Get lowercase key
        const key = e.key.toLowerCase();

        // Handle sequence based logic
        if (currentShortcutSequence) {
          // If we have a current sequence, append this key and check for matches
          const newSequence = currentShortcutSequence + key;

          // Check for exact match
          const exactHandler = homepageShortcuts.getHandler(newSequence);
          if (exactHandler && exactHandler.isEnabled()) {
            // Close dropdowns before executing action
            closeDropdowns();
            exactHandler.execute();
            return;
          }

          // Check if it's a partial match that could lead to a full match
          if (homepageShortcuts.isPartialMatch(newSequence)) {
            setShortcutSequence(newSequence);

            // Open appropriate dropdown based on first character of sequence
            if (newSequence.startsWith("f") && !featuresDropdownOpen) {
              toggleFeaturesDropdown();
            } else if (newSequence.startsWith("c") && !companyDropdownOpen) {
              toggleCompanyDropdown();
            } else if (newSequence.startsWith("u") && !useCasesDropdownOpen) {
              toggleUseCasesDropdown();
            }

            return;
          }

          // If no match and no potential match, reset
          resetShortcutState();
          closeDropdowns();
          return;
        } else {
          // No current sequence, start a new one

          // First handle escape key to exit shortcut mode
          if (key === "escape") {
            setShortcutMode(false);
            closeDropdowns();
            return;
          }

          // Check for direct shortcuts
          const handler = homepageShortcuts.getHandler(key);
          if (handler && handler.isEnabled()) {
            // Special dropdown handlers
            if (key === "f") {
              toggleFeaturesDropdown();
              setShortcutSequence(key);
              return;
            } else if (key === "c") {
              toggleCompanyDropdown();
              setShortcutSequence(key);
              return;
            } else if (key === "u") {
              toggleUseCasesDropdown();
              setShortcutSequence(key);
              return;
            }

            // Close all dropdowns before executing regular actions
            closeDropdowns();
            // Regular action handlers
            handler.execute();
            return;
          }

          // Check if it could start a sequence
          if (homepageShortcuts.isPartialMatch(key)) {
            setShortcutSequence(key);

            // Open appropriate dropdown
            if (key === "f") {
              toggleFeaturesDropdown();
            } else if (key === "c") {
              toggleCompanyDropdown();
            } else if (key === "u") {
              toggleUseCasesDropdown();
            }

            return;
          }
        }
      }
    }
  };

  // Handle document clicks
  const handleDocumentClick = (e: MouseEvent) => {
    const target = e.target as Node;
    const featuresDropdown = document.getElementById("features-dropdown");
    const featuresButton = document.getElementById("features-button");
    const companyDropdown = document.getElementById("company-dropdown");
    const companyButton = document.getElementById("company-button");
    const useCasesDropdown = document.getElementById("usecases-dropdown");
    const useCasesButton = document.getElementById("usecases-button");

    // Close dropdowns when clicking outside
    if (
      featuresDropdownOpen &&
      featuresDropdown &&
      !featuresDropdown.contains(target) &&
      featuresButton &&
      !featuresButton.contains(target)
    ) {
      featuresDropdownOpen = false;
    }

    if (
      companyDropdownOpen &&
      companyDropdown &&
      !companyDropdown.contains(target) &&
      companyButton &&
      !companyButton.contains(target)
    ) {
      companyDropdownOpen = false;
    }

    if (
      useCasesDropdownOpen &&
      useCasesDropdown &&
      !useCasesDropdown.contains(target) &&
      useCasesButton &&
      !useCasesButton.contains(target)
    ) {
      useCasesDropdownOpen = false;
    }

    // If shortcut mode is active and we click outside, exit shortcut mode
    if (isInShortcutMode) {
      // Don't exit if we're clicking on a shortcut-related element
      const isShortcutRelated =
        (featuresButton && featuresButton.contains(target)) ||
        (companyButton && companyButton.contains(target)) ||
        (useCasesButton && useCasesButton.contains(target)) ||
        (featuresDropdown && featuresDropdown.contains(target)) ||
        (companyDropdown && companyDropdown.contains(target)) ||
        (useCasesDropdown && useCasesDropdown.contains(target));

      if (!isShortcutRelated) {
        setShortcutMode(false);
      }
    }
  };

  // Handle blur event to reset shortcut mode
  const handleBlur = () => {
    setShortcutMode(false);
    closeDropdowns();
  };

  const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      featuresDropdownOpen = false;
      companyDropdownOpen = false;
      useCasesDropdownOpen = false;

      if (isInShortcutMode) {
        setShortcutMode(false);
      }
      closeDropdowns();
    }
  };

  $effect(() => {
    if (!isInShortcutMode) {
      closeDropdowns();
      return;
    }

    if (currentShortcutSequence === "f") {
      featuresDropdownOpen = true;
      companyDropdownOpen = false;
      useCasesDropdownOpen = false;
    } else if (currentShortcutSequence === "c") {
      featuresDropdownOpen = false;
      companyDropdownOpen = true;
      useCasesDropdownOpen = false;
    } else if (currentShortcutSequence === "u") {
      featuresDropdownOpen = false;
      companyDropdownOpen = false;
      useCasesDropdownOpen = true;
    } else if (!currentShortcutSequence) {
      closeDropdowns();
    }
  });

  onMount(() => {
    if (browser) {
      window.addEventListener("keydown", handleKeyDown);
      window.addEventListener("keydown", handleEscapeKey);
      window.addEventListener("blur", handleBlur);
      document.addEventListener("click", handleDocumentClick);
    }
  });

  onDestroy(() => {
    if (browser) {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keydown", handleEscapeKey);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("click", handleDocumentClick);
      resetShortcutState();
    }
  });
</script>

{#if navigationState === "loading"}
  <div out:fade={{ delay: 100 }}>
    <PageLoader />
  </div>
{/if}

<div
  class={cn(
    `transition-default z-50 flex h-14 items-center justify-between bg-theme-background-primary py-4 text-sm font-normal leading-normal dark:bg-theme-background-tertiary md:h-18 lg:overflow-y-visible border-b border-theme-border-subtle shadow-sm`,
    variant === "dark" ? "!bg-[var(--dark-background-secondary)]" : "",
    className,
  )}
  class:hidden={isHeaderless && !sidebar.isMobile}
>
  <div class="transition-default mx-auto w-full max-w-full px-2 lg:px-8">
    <div class="z-10 flex items-center justify-between">
      <!-- Logo Section - Mobile -->
      <HeaderLogo
        {logo}
        {sidebar}
        {hamburgerMenuIcon}
        {isInShortcutMode}
        {currentShortcutSequence}
        {variant}
        isMobile={true}
      />

      <!-- Logo Section - Desktop -->
      <HeaderLogo
        {logo}
        {sidebar}
        {hamburgerMenuIcon}
        {isInShortcutMode}
        {currentShortcutSequence}
        {variant}
        isMobile={false}
      />

      <!-- Navigation -->
      {#if !$Current.isLoggedIn}
        <HeaderNavigation {isInShortcutMode} {currentShortcutSequence} {isBlogPage} {routes} {goto} />
      {/if}

      <!-- Auth Buttons -->
      <div class="z-10 flex items-center">
        {#if !$Current.isLoggedIn}
          <div class="flex items-center space-x-2">
            <a
              class={cn(
                "c-button transition-default button-login group relative hidden items-center justify-center rounded-base border border-transparent px-6 py-2.5 text-base hover:border-theme-text-primary sm:inline-flex",
                "text-theme-text-primary hover:border-theme-text-primary dark:text-theme-text-primary dark:hover:border-theme-text-primary",
              )}
              href={routes.signIn}
              onclick={() => {
                goto(routes.signIn);
                closeDropdowns();
              }}
            >
              <span class="flex items-center justify-center">
                <span class={cn("text-base font-normal uppercase", isBlogPage ? "text-white" : "")}>LOG IN</span>
              </span>
              {#if isInShortcutMode && !currentShortcutSequence}
                <div
                  class="absolute -bottom-2 left-1/2 flex size-6 -translate-x-1/2 translate-y-full items-center justify-center bg-yellow-400 text-black"
                >
                  <span class="text-xs">L</span>
                </div>
              {/if}
            </a>
            <a
              class="c-button transition-default hover:bg-theme-accent-hover button-signup group relative hidden h-12 items-center justify-center rounded-base border border-black bg-theme-green px-6 py-2 text-black sm:inline-flex"
              href={routes.signUp}
              onclick={() => {
                goto(routes.signUp);
                closeDropdowns();
              }}
            >
              <span class="flex items-center justify-center font-normal">
                <span class="text-base uppercase">Sign Up <span class="hidden uppercase md:inline">for Free</span></span
                >
              </span>
              {#if isInShortcutMode && !currentShortcutSequence}
                <div
                  class="absolute -bottom-2 left-1/2 flex size-6 -translate-x-1/2 translate-y-full items-center justify-center bg-yellow-400 text-black"
                >
                  <span class="text-xs">S</span>
                </div>
              {/if}
            </a>
          </div>
        {/if}
      </div>
    </div>
  </div>
</div>
